import { t } from '@/utils/i18n'
import { useState, useEffect } from 'react'
import { useMount } from 'ahooks'
import { useNavigate, useParams } from 'react-router-dom'
import { useCorpSettingStore, useCorpInfoStore, useCartStore } from '@/stores'
import { LeftOutline } from 'antd-mobile-icons'
import { Swiper, Toast, Image as AntdImage, ImageViewer } from 'antd-mobile'
import DetailBar from './components/DetailBar/DetailBar'
import PlaceOrder from '@/components/PlaceOrder/PlaceOrder'
import ProductNotFound from './components/ProductNotFound/ProductNotFound'
import OverseasShipping from './components/OverseasShipping/OverseasShipping'
import ProductGraphic from './components/ProductGraphic/ProductGraphic'
import AttrList from './components/CloudAttrs/CloudAttrs'
import ShippingInfo from '@/components/ShippingInfo/ShippingInfo'
import DetailSkeleton from './components/DetailSkeleton/DetailSkeleton'
import {
  apis,
  utils,
  image as imgUtil,
  cal,
  productUtil,
  uploadEt,
  common,
} from '@/utils'
import eventUtil from '@/utils/event'
import type {
  UnitPriceItem,
  SaleProduct,
  CloudProduct,
  ProductItem,
} from '@/types/product'
import styles from './Detail.module.less'
// 导入图片
import defaultImg from '@/assets/img/default/default.png'
import useRefState from '@/hooks/useRefState'

const { productApi, orderApi } = apis
const { to, isDisableBodyScroll } = utils
const { imgFormatter } = imgUtil

interface CloudAttrItem {
  [key: string]: string
}

function Detail() {
  const { productId } = useParams()
  const { corpSetting } = useCorpSettingStore()
  const { corpInfo } = useCorpInfoStore()
  const { corpId, customerId } = corpInfo
  const {
    currencySymbol,
    qualityPrecision,
    stockOn,
    pricePrecision,
    cloudStoreStockType,
  } = corpSetting
  const { cart, setCart } = useCartStore()
  const { cartId } = cart
  const navigate = useNavigate()
  const [notFound, setNotFound] = useState(false)
  const [showPlaceOrder, setShowPlaceOrder] = useState(false)
  const [product, setProduct] = useState<CloudProduct>() // 货品实例
  const [saleProduct, setSaleProduct] = useState<SaleProduct | null>() // 加入购物车货品实例
  // const saleProductRef = useRef(saleProduct)
  const [chooseUnit, setChooseUnit] = useState<UnitPriceItem>() // 选择的单位
  const [mediaList, setMediaList] = useState<string[]>([]) // 媒体列表
  const [cloudAttrs, setCloudAttrs] = useState<CloudAttrItem[]>([]) // 需要显示的属性
  const [detailImageList, setDetailImageList] = useState<string[]>([]) // 图文详情图片(包含货品主图)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isMultiPrice, setIsMultiPrice, isMultiPriceRef] = useRefState(false) // 是否不同规格不同价格
  const [disable, setDisable] = useState(false) // 是否可以 add cart
  const [contentLoader, setContentLoader] = useState(true)
  const [showFlyCart, setShowFlyCart] = useState(false)

  let loading = false // 请求锁

  // 轮播图
  const items = mediaList.map((item, index) => (
    <Swiper.Item key={index}>
      <AntdImage
        fit="cover"
        width={'100vw'}
        height={'100vw'}
        src={item}
        lazy={true}
        onClick={() => {
          handlePreviewImg(index)
        }}
      />
    </Swiper.Item>
  ))

  function handleGoBack() {
    navigate(-1)
  }

  function handlePreviewImg(index: number) {
    if (mediaList[0] === defaultImg) {
      return
    }

    // 如果 productAttr8 有值，则说明图片是P过的，图片地址拼接’/poster‘显示P过的图
    const images = mediaList.map((src: string) => {
      return `${src}${product?.productAttr8 ? '/poster' : ''}`
    })

    ImageViewer.Multi.show({
      images: images,
      defaultIndex: index,
    })
  }

  const createdSwiperIndicator = (total: number, current: number) => (
    <div className={styles['custom-indicator']}>{`${
      current + 1
    } / ${total}`}</div>
  )

  // 获取货品数据
  async function getGoodsDetail() {
    const sendData = {
      cartId,
      corpId,
      customerId,
      productId,
    }

    const [err, res] = await to(productApi.prodDetail(sendData))

    if (err) {
      setNotFound(true)
      return
    }

    setContentLoader(false)

    const { cloudProduct, saleProduct } = res.data

    // 后端存在数据维护不正确的问题，起订单位的下标会出现大于等于单位数组的长度，需要重置下标为0
    if (
      cloudProduct.unitPriceArray &&
      cloudProduct.productMoqUnitIndex > 0 &&
      cloudProduct.productMoqUnitIndex >= cloudProduct.unitPriceArray?.length
    ) {
      cloudProduct.productMoqUnitIndex = 0
    }

    setProduct(cloudProduct)
    setSaleProduct(saleProduct)
    setIsMultiPrice((cloudProduct.productStatus & 128) === 128)

    await initMediaList(cloudProduct)
    initChooseUnit(cloudProduct, saleProduct)
    initCloudAttrsSpecification(cloudProduct, cloudProduct.unitPriceArray)
    updateBtn(cloudProduct) // 判断加入购物车按钮是否置灰
  }

  // 初始化媒体列表
  async function initMediaList(cloudProduct: CloudProduct) {
    const { video, imageList } = cloudProduct
    const mediaList = [] // 货品图片和视频

    const mediaListPromiseArr = (imageList || []).map(
      (item: string, index: number) => {
        imgFormatter(item)
          .then((res) => {
            mediaList[index] = res
          })
          .catch(() => {
            mediaList[index] = item
          })
      },
    )

    await Promise.all(mediaListPromiseArr)

    if (video) {
      const [err1, res1] = await to(imgFormatter(video, 480))
      mediaList.unshift(err1 ? video : res1)
    }

    // 货品图文详情
    let detailImageList: string[] = []
    const detailImageListPromiseArr = (cloudProduct.detailImageList || []).map(
      (item: string, index: number) => {
        return imgFormatter(item)
          .then((res) => {
            detailImageList[index] = res || ''
          })
          .catch(() => {
            detailImageList[index] = item
          })
      },
    )

    await Promise.all(detailImageListPromiseArr)

    if (mediaList.length === 0) {
      mediaList.push(defaultImg)
    } else {
      detailImageList = detailImageList.concat(mediaList)
    }

    setMediaList(mediaList)
    setDetailImageList(detailImageList)
  }

  // 根据默认单位索引取单位对象
  function initChooseUnit(cloudProduct: CloudProduct, saleProd: SaleProduct) {
    const currentChooseUnit = productUtil.setChooseUnit(
      { cloudProduct, saleProd },
      isMultiPriceRef.current,
      cloudStoreStockType,
      qualityPrecision,
      pricePrecision,
    )
    setChooseUnit(currentChooseUnit)
  }

  // 初始化显示属性和装箱规格
  function initCloudAttrsSpecification(
    initProduct: CloudProduct,
    unitPriceArray: UnitPriceItem[],
  ) {
    // 组装装箱规格
    const specification = productUtil.getSpecification(unitPriceArray)
    const currentProduct = initProduct || product
    // 获取需要显示的自定义属性
    const keyValues: CloudAttrItem[] = []
    const list = corpSetting.cloudAttrs.filter((item: any) => {
      return item.attrStatus === 1 || item.cloudShowStatus === 1
    })

    list.unshift({
      attrKey: 'packaging',
      attrValue: specification,
    })

    list.forEach((item: any) => {
      if (item.attrKey === 'packaging') {
        keyValues.push({
          key: 'packaging',
          label: t('装箱规格'),
          value: specification,
        })
      } else {
        keyValues.push({
          key: item.attrKey,
          label: item.attrKeyValue,
          value: currentProduct ? currentProduct[item.attrKey] : '',
        })
      }
    })

    const sellingPointIndex = keyValues.findIndex((item: any) => {
      return item.key === 'sellingPoint'
    })

    if (currentProduct.productWeight) {
      const productWeight = {
        key: 'productWeight',
        label: t('重量'),
        value: String(currentProduct.productWeight) + 'kg',
      }

      if (sellingPointIndex) {
        keyValues.splice(sellingPointIndex, 0, productWeight)
      } else {
        keyValues.push(productWeight)
      }
    }

    if (
      currentProduct.productLength &&
      currentProduct.productWidth &&
      currentProduct.productHigh
    ) {
      const volume = {
        key: 'volume',
        label: t('体积'),
        value: `${currentProduct.productLength}cm * ${currentProduct.productWidth}cm * ${currentProduct.productHigh}cm`,
      }

      if (sellingPointIndex) {
        keyValues.splice(sellingPointIndex, 0, volume)
      } else {
        keyValues.push(volume)
      }
    }

    setCloudAttrs(keyValues)
  }

  function updateBtn(product: CloudProduct) {
    if (stockOn) {
      const {
        productLockStock,
        unitPriceArray,
        productMoqUnitIndex,
        productMoqQty,
      } = product
      let currentChooseUnit = null
      let { productStock: stock } = product

      if (unitPriceArray) {
        currentChooseUnit = unitPriceArray[productMoqUnitIndex]
      }

      // 如果云店设置显示的是可售库存，则可售库存=库存-锁定库存
      if (cloudStoreStockType === 1 && productLockStock) {
        stock = cal.minus(stock, productLockStock)
      }

      if (currentChooseUnit) {
        stock = Number(
          cal.getDown(
            qualityPrecision,
            cal.divide(stock, currentChooseUnit.unitRatio),
          ),
        )
      }
      // 如果设置了起订量
      setDisable(Number(productMoqQty) > stock || stock <= 0)
    }
  }

  function handleAddToCart() {
    // 没有登录跳转到登录页
    if (!corpInfo?.accountId) {
      // navigate(routesMap.Login);
      common.logout()
      return
    }

    // 如果总库存小于起订量
    if (disable) {
      Toast.show(t('库存不足无法下单'))
      return
    }

    // 快速加购：
    // 1.没有多规格
    // 2.没有多单位 (product?.unitPriceArray && product?.unitPriceArray.length <= 1 &&) (目前没有选择单位功能)
    // 3.购物车里不存在数量
    // 满足上述条件时的，直接+1（或起订量）到购物车中
    if (
      !(product?.specDetailList && product.specDetailList.length) &&
      !saleProduct?.totalQty
    ) {
      handleQuickAdd()
    } else {
      // 不满足快速加购条件，弹出模态框
      setShowPlaceOrder(true)
    }
  }

  async function handleQuickAdd() {
    if (!product) return
    if (loading) return

    const data = {
      cloudProduct: product,
      saleProduct: saleProduct,
    }
    let quantity = 1

    if (chooseUnit?.productMoqQty) {
      quantity = chooseUnit.productMoqQty || 1
    }

    // 修改选择单位索引
    if (product.productMoqUnitIndex !== undefined) {
      productUtil.editSaleUnit(data, product.productMoqUnitIndex)
    }

    productUtil.editSaleQuantity(data, quantity)

    Toast.show({
      icon: 'loading',
      content: t('加载中…'),
      duration: 0,
    })
    loading = true

    const res = await requestAddProd(data)

    loading = false

    if (res) {
      Toast.show({
        content: t('添加成功，在购物车等你'),
        duration: 1000,
      })
    } else {
      Toast.clear()
      setShowPlaceOrder(true)
    }
  }

  // 请求添加/更新货品
  async function requestAddProd(product: ProductItem) {
    const params = {
      saleProduct: product.saleProduct,
    }
    params.saleProduct.cartId = cartId
    params.saleProduct.corpId = corpId
    params.saleProduct.selected = 1

    const [err, res] = await to(orderApi.addUpdate(params))

    if (err) return false

    const { productCount, saleProduct } = res.data
    setShowFlyCart(true)
    updateProduct(saleProduct)
    updateCartProductCount(productCount)
    return true
  }

  // 更新购物车数量
  function updateCartProductCount(productCount: number) {
    cart.productCount = productCount
    setCart(cart)
  }

  // 删除购物车货品后更新 SaleProduct
  function updateProduct(saleProd?: SaleProduct) {
    if (saleProd) {
      setSaleProduct(saleProd)
      eventUtil.emit('updateProduct', [{ saleProduct: saleProd, productId }])
      return
    }

    setSaleProduct(null)
    eventUtil.emit('updateProduct', [{ saleProduct: {}, productId }])
  }

  // 数据初始化
  async function init() {
    Toast.show({
      icon: 'loading',
      content: t('加载中…'),
      duration: 0,
    })

    await getGoodsDetail()

    Toast.clear()
  }

  useMount(() => {
    uploadEt({ event_type: 5, products: [{ product_id: productId }] }) // 埋点，货品详情页打开次数

    document.title = t('选购')
    init()
  })

  useEffect(() => {
    // 禁用 body 滚动
    isDisableBodyScroll(showPlaceOrder)
  }, [showPlaceOrder])
  function handleCompleteFlyCart() {
    setShowFlyCart(false)
  }

  return (
    <div className="detail-page">
      {notFound && <ProductNotFound />}
      {!notFound && !contentLoader && (
        <div className={styles['detail-wrap']}>
          <div className={styles['top-bar']}>
            <div className={styles['icon-wrap']} onClick={handleGoBack}>
              <LeftOutline color="#ffffff" fontSize={18} />
            </div>
          </div>
          <div className={styles['swiper-wrap']}>
            <Swiper
              style={{ '--width': '100vw', '--height': '100vw' }}
              indicator={createdSwiperIndicator}
            >
              {items}
            </Swiper>
          </div>
          <div className={styles['card-wrap'] + ' ' + styles['detail-info']}>
            <div className={styles['price-wrap']}>
              {/* 货品报价和单位 */}
              <p className={styles['price']}>
                {currencySymbol}{' '}
                {corpInfo.accountId ? chooseUnit?.priceText : '***'}
                {chooseUnit?.priceUnitName
                  ? '/' + chooseUnit.priceUnitName
                  : ''}
              </p>

              {/* 原价 */}
              {chooseUnit && chooseUnit.originPriceText.length > 0 && (
                <p className={styles['original-price']}>
                  {currencySymbol}{' '}
                  {corpInfo.accountId ? chooseUnit?.originPriceText : '***'}{' '}
                  <span
                    className={styles['price-tag']}
                    style={{ fontSize: '10px' }}
                  >
                    -{chooseUnit?.discount}
                  </span>
                </p>
              )}
            </div>
            {/* 隐藏VAT */}
            {/* <div className={styles['tag-wrap']}>
              <span className={styles["tax-tag"]}>{t("不含税")}</span>
            </div> */}
            {/* 货品标题 */}
            <h1 className={styles['product-title'] + ' ep-2'}>
              {!!product?.cloudProduct && (
                <span className={productUtil.getRecommendClass(product)}>
                  {productUtil.getRecommendTag(product)}
                </span>
              )}

              {product?.productName}
            </h1>

            {/* 起订量 */}
            {product && Number(product.productMoqQty) > 0 && (
              <p className={styles['text-wrap']}>
                MOQ:
                {product.productMoqQty}
                {product.unitPriceArray &&
                product.unitPriceArray[product.productMoqUnitIndex].unitName
                  ? product.unitPriceArray[product.productMoqUnitIndex].unitName
                  : ''}
              </p>
            )}
          </div>

          {/* 海外发货显示跨境物流示意 */}
          {/* {product?.deliveryType !== 1 && <OverseasShipping />} */}

          {/* 自定义属性列表 */}
          {product && <AttrList attrList={cloudAttrs} />}

          {/* 货品图片详细(包含主图) */}
          <ProductGraphic
            mediaList={detailImageList}
            visible={!!detailImageList.length}
          />

          {/* 运费说明 */}
          {corpSetting.cod && (
            <ShippingInfo deliveryType={product?.deliveryType} />
          )}

          <div className={styles['card-wrap']}>
            <h2 className={styles['title']}>{t('大额订单说明')}</h2>
            <div className={styles['desc']}>
              {t('订单超过1亿，店长会主动联系您协助完成交易')}
            </div>
          </div>
          <DetailBar
            onAddToCart={handleAddToCart}
            showFlyCart={showFlyCart}
            onCompleteFlyCart={handleCompleteFlyCart}
          />
        </div>
      )}

      {contentLoader && <DetailSkeleton />}
      {!!product && (
        <PlaceOrder
          showPlaceOrder={showPlaceOrder}
          item={{ cloudProduct: product, saleProduct: saleProduct }}
          onClosePlaceOrder={() => setShowPlaceOrder(false)}
          onRemoveCartProduct={updateProduct}
          onUpdateProd={updateProduct}
        />
      )}
    </div>
  )
}

export default Detail
